import { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { http } from "../api";
import { PageHeader } from "../shared";
import { PieChart, Info } from "lucide-react";

const NABADWIP = [23.4083, 88.3722];
const COLORS = { supporter: "#0F766E", neutral: "#3B82BB", undecided: "#B3A484", opposition: "#BD4F46" };
const LABELS = { supporter: "Supporter", neutral: "Neutral", undecided: "Undecided", opposition: "Opposition" };

export default function SentimentMap() {
  const [rows, setRows] = useState([]);

  useEffect(() => { http.get("/sentiment/booths").then((r) => setRows(r.data)); }, []);

  const totals = rows.reduce((acc, r) => {
    acc.total += r.total; acc.supporter += r.supporter; acc.neutral += r.neutral;
    acc.undecided += r.undecided; acc.opposition += r.opposition; return acc;
  }, { total: 0, supporter: 0, neutral: 0, undecided: 0, opposition: 0 });

  const barPct = (k) => Math.round(((totals[k] || 0) / Math.max(totals.total, 1)) * 100);
  const dominantBooths = rows.reduce((acc, r) => { acc[r.dominant] = (acc[r.dominant] || 0) + 1; return acc; }, {});

  return (
    <>
      <PageHeader eyebrow="POLITICAL LANDSCAPE" title="Sentiment map"
        subtitle="Each booth sized by voters, coloured by dominant inclination"
        actions={<div className="legend-inline">
          {Object.entries(LABELS).map(([k, v]) => (
            <span key={k}><i className="dot" style={{ background: COLORS[k] }} /> {v}</span>
          ))}
        </div>} />

      <div className="stats" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
        {Object.keys(LABELS).map((k) => (
          <div key={k} className="stat" data-testid={`sentiment-stat-${k}`}>
            <div className="stat-icon" style={{ background: COLORS[k] }}><PieChart size={19} /></div>
            <span>{LABELS[k]}</span>
            <strong>{barPct(k)}%</strong>
            <small className="trend">{totals[k]} voters · {dominantBooths[k] || 0} booths</small>
          </div>
        ))}
      </div>

      <div className="dashboard-grid">
        <section className="panel" style={{ padding: 0, overflow: "hidden" }}>
          <MapContainer center={NABADWIP} zoom={14} style={{ height: 520, width: "100%" }} data-testid="sentiment-map-container">
            <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {rows.map((r) => (
              <CircleMarker key={r.booth} center={[r.lat, r.lng]} radius={12 + r.total * 1.5}
                pathOptions={{ color: COLORS[r.dominant], fillColor: COLORS[r.dominant], fillOpacity: 0.55, weight: 2 }}>
                <Tooltip permanent direction="center" className="booth-label">{r.booth}</Tooltip>
                <Popup>
                  <b>Booth {r.booth}</b><br />
                  <small>{r.total} voters · Dominant: <b style={{ color: COLORS[r.dominant] }}>{LABELS[r.dominant]}</b></small><br />
                  <span style={{ color: COLORS.supporter }}>■</span> Supporter {r.supporter} ({r.supporter_pct}%)<br />
                  <span style={{ color: COLORS.neutral }}>■</span> Neutral {r.neutral}<br />
                  <span style={{ color: COLORS.undecided }}>■</span> Undecided {r.undecided}<br />
                  <span style={{ color: COLORS.opposition }}>■</span> Opposition {r.opposition} ({r.opposition_pct}%)
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </section>

        <section className="panel">
          <div className="panel-head"><h2>Booth breakdown</h2><p>{rows.length} booths in Ward 7</p></div>
          <div className="sent-list">
            {rows.map((r, i) => (
              <div key={r.booth} className="sent-row" data-testid={`sentiment-row-${i}`}>
                <div className="sent-head">
                  <b>Booth {r.booth}</b>
                  <span className="chip" style={{ background: COLORS[r.dominant] + "20", color: COLORS[r.dominant] }}>{LABELS[r.dominant]}</span>
                  <small className="hint">{r.total} voters</small>
                </div>
                <div className="sent-bar">
                  <span style={{ width: `${(r.supporter / r.total) * 100}%`, background: COLORS.supporter }} />
                  <span style={{ width: `${(r.neutral / r.total) * 100}%`, background: COLORS.neutral }} />
                  <span style={{ width: `${(r.undecided / r.total) * 100}%`, background: COLORS.undecided }} />
                  <span style={{ width: `${(r.opposition / r.total) * 100}%`, background: COLORS.opposition }} />
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}
